import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  InputAdornment,
  Skeleton,
  TextField,
  Typography,
  styled,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useAccount } from "../../context";

import logo from "../../public/logo.svg";
import authService from "../../services/auth";
import { formatCPF, validateCPF } from "../../utils/validation";

const Component = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  height: 100%;
  padding: 40px 20px;
  background: linear-gradient(135deg, #0a0118 0%, #1b0032 100%);

  @media (max-width: 600px) {
    padding: 24px 12px; /* Padding otimizado para mobile */
  }
`;

const Container = styled(Box)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding: 48px 32px;
  max-width: 500px;
  width: 100%;
  background: rgba(20, 0, 40, 0.8);
  backdrop-filter: blur(12px);
  border-radius: 24px; /* Border-radius mais moderno */
  box-shadow: 0 12px 40px rgba(179, 136, 255, 0.2); /* Sombra mais sofisticada */
  border: 1px solid rgba(255, 255, 255, 0.08);

  @media (max-width: 600px) {
    padding: 32px 20px; /* Padding otimizado para mobile */
    border-radius: 20px; /* Border-radius ajustado para mobile */
  }
`;

const Logo = styled("img")`
  width: 160px;
  margin-bottom: 32px;
`;

const Title = styled(Typography)`
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 12px;
  color: #ffffff;
  background: linear-gradient(90deg, #d1b3ff, #a079ff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Subtitle = styled(Typography)`
  font-size: 14px;
  color: #cdb5ff;
  margin-bottom: 36px;
  max-width: 380px;
  line-height: 1.6;
`;

const StyledTextField = styled(TextField)`
  width: 100%;
  margin-bottom: 20px;

  & .MuiOutlinedInput-root {
    border-radius: 12px;
    background-color: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(179, 136, 255, 0.2);
    color: #ffffff;

    &:hover {
      background-color: rgba(179, 136, 255, 0.08);
      border-color: rgba(179, 136, 255, 0.3);
    }

    &.Mui-focused {
      background-color: rgba(179, 136, 255, 0.1);
      border-color: #b388ff;
    }

    & input {
      color: #ffffff;
      padding: 14px;

      &::placeholder {
        color: rgba(179, 136, 255, 0.5);
      }

      &:-webkit-autofill,
      &:-webkit-autofill:hover,
      &:-webkit-autofill:focus {
        -webkit-text-fill-color: #ffffff;
        -webkit-box-shadow: 0 0 0px 1000px rgba(179, 136, 255, 0.1) inset;
        transition: background-color 5000s ease-in-out 0s;
      }
    }
  }

  & .MuiInputLabel-root {
    color: #cdb5ff;
    &.Mui-focused {
      color: #b388ff;
    }
    &.Mui-error {
      color: #ff5252;
    }
  }

  & .MuiOutlinedInput-notchedOutline {
    border-color: rgba(179, 136, 255, 0.2);
    transition: border-color 0.3s ease;
  }

  & .MuiIconButton-root {
    color: #b388ff;
    &:hover {
      color: #ffffff;
      background-color: rgba(179, 136, 255, 0.1);
    }
  }

  &.Mui-error .MuiOutlinedInput-root {
    background-color: rgba(255, 82, 82, 0.05);
    &:hover {
      background-color: rgba(255, 82, 82, 0.08);
    }
    &.Mui-focused {
      background-color: rgba(255, 82, 82, 0.1);
    }
  }
`;

const LoginButton = styled(Button)`
  width: 100%;
  padding: 16px; /* Padding aumentado para melhor toque */
  margin-top: 16px; /* Margin aumentada */
  background: linear-gradient(90deg, #b388ff 0%, #7c4dff 100%);
  color: white;
  border-radius: 16px; /* Border-radius mais moderno */
  text-transform: none;
  font-size: 16px;
  font-weight: 600;
  transition: all 0.3s ease;
  box-shadow: 0 6px 20px rgba(179, 136, 255, 0.3); /* Sombra mais sofisticada */

  &:hover {
    background: linear-gradient(90deg, #7c4dff 0%, #651fff 100%);
    box-shadow: 0 8px 24px rgba(179, 136, 255, 0.4);
    transform: translateY(-2px); /* Transform mais sutil */
  }

  &:disabled {
    background: #2c0549;
    color: rgba(255, 255, 255, 0.5);
    box-shadow: none;
  }

  @media (max-width: 600px) {
    padding: 16px; /* Manter padding adequado em mobile */
    font-size: 16px; /* Manter tamanho adequado */
  }
`;

const ErrorMessage = styled(Typography)`
  color: #ff6b6b;
  font-size: 13px;
  margin-top: 8px;
  text-align: left;
  width: 100%;
  background: rgba(255, 107, 107, 0.08);
  padding: 10px 14px;
  border-radius: 10px;
  border: 1px solid rgba(255, 107, 107, 0.3);
`;

const Footer = styled(Box)`
  margin-top: 40px;
  text-align: center;
  font-size: 13px;
  color: #cdb5ff;
`;

const FooterLink = styled("a")`
  color: #cdb5ff;
  text-decoration: none;
  font-weight: 500;

  &:hover {
    color: #ffffff;
    text-decoration: underline;
  }
`;

const dialogStyle = {
  marginTop: "3%",
  height: "auto",
  maxWidth: "sm",
  borderRadius: 16,
  boxShadow: "0 8px 32px rgba(44, 5, 73, 0.3)",
  padding: 0,
  overflow: "hidden",
  background: "transparent",
};

const LoginDialog: React.FC = () => {
  const { authState, login } = useAccount();

  const [loading, setLoading] = useState(false);
  const [showSkeleton, setShowSkeleton] = useState(false);
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [identifierError, setIdentifierError] = useState<string | null>(null);

  // Simular loading inicial
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSkeleton(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (authState === "login") {
      setError(null);
      setIdentifierError(null);
    }
  }, [authState]);

  // Renderizar skeleton se necessário
  if (showSkeleton) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          backgroundColor: "#f0f2f5",
          gap: 3,
        }}
      >
        <Box
          sx={{
            width: "400px",
            backgroundColor: "#ffffff",
            borderRadius: 2,
            p: 4,
            boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
            "@media (max-width: 600px)": {
              width: "calc(100vw - 16px)", // Ajustar largura para mobile
              p: 2, // Reduzir padding em mobile
              borderRadius: 1.5, // Bordas menos arredondadas
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
              mb: 4,
            }}
          >
            <Skeleton variant="circular" width={80} height={80} />
            <Skeleton variant="text" width={200} height={24} />
            <Skeleton variant="text" width={150} height={16} />
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Skeleton variant="rectangular" width="100%" height={56} />
            <Skeleton variant="rectangular" width="100%" height={56} />
            <Skeleton variant="rectangular" width="100%" height={48} />
          </Box>
        </Box>
      </Box>
    );
  }

  const handleIdentifierChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    const isCpf =
      /^\d+$/.test(value.replace(/[^\d]/g, "")) && !value.includes("@");

    if (isCpf && value.length > 0) {
      value = formatCPF(value);
    }

    setIdentifier(value);

    if (value.includes("@") || /[a-zA-Z]/.test(value)) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (value.length > 5 && !emailRegex.test(value)) {
        setIdentifierError("Email inválido");
      } else {
        setIdentifierError(null);
      }
    } else if (isCpf) {
      const result = validateCPF(value);
      if (!result.isValid) {
        setIdentifierError(result.error || null);
      } else {
        setIdentifierError(null);
      }
    } else {
      setIdentifierError(null);
    }
    setError(null);
  };

  const handleLogin = async () => {
    if (!identifier.trim() || !password.trim()) {
      authService.showValidationError("Por favor, preencha todos os campos.");
      return;
    }

    if (identifierError) {
      return;
    }

    setLoading(true);
    setError(null);
    setIdentifierError(null);

    try {
      const cleanIdentifier = identifier.includes("@")
        ? identifier.trim()
        : identifier.replace(/[^\d]/g, "");
      await login(cleanIdentifier, password);
    } catch (err) {
      // O AuthService já trata o erro e mostra a mensagem apropriada
      if (err instanceof Error) {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && !loading) {
      handleLogin();
    }
  };

  return (
    <Dialog open maxWidth="sm" slotProps={{ paper: { sx: dialogStyle } }}>
      <Component>
        <Container>
          <Logo src={logo} alt="Virtus Automation" />
          <Title>Bem-vindo ao WhatsApp Virtus</Title>
          <Subtitle>
            Utilize suas credenciais do CRM para acessar o sistema e gerenciar
            suas conversas com inteligência artificial.
          </Subtitle>

          <StyledTextField
            id="login-identifier"
            name="identifier"
            label="Email ou CPF"
            variant="outlined"
            value={identifier}
            onChange={handleIdentifierChange}
            onKeyDown={handleKeyDown}
            placeholder="Digite seu Email ou CPF"
            error={!!identifierError || !!error}
            helperText={identifierError}
            disabled={loading}
            inputProps={{
              maxLength: 100,
            }}
          />

          <StyledTextField
            id="login-password"
            name="password"
            label="Senha"
            type={showPassword ? "text" : "password"}
            variant="outlined"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setError(null);
            }}
            onKeyDown={handleKeyDown}
            placeholder="Digite sua senha"
            error={!!error}
            disabled={loading}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    disabled={loading}
                  >
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {error && <ErrorMessage>{error}</ErrorMessage>}

          <LoginButton
            onClick={handleLogin}
            disabled={
              loading ||
              !identifier.trim() ||
              !password.trim() ||
              !!identifierError
            }
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Entrar"
            )}
          </LoginButton>
        </Container>

        <Footer>
          <Typography>
            © {new Date().getFullYear()}{" "}
            <FooterLink
              href="https://www.virtusautomation.com/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Virtus Automation & IA
            </FooterLink>
            . Todos os direitos reservados.
          </Typography>
        </Footer>
      </Component>
    </Dialog>
  );
};

export default LoginDialog;
